/**
 * File: engineeringPage.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v3.1.2
 * Created: 2025-12-04
 * Last Updated: 2026-02-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Icons
import { FaBarsProgress } from "react-icons/fa6";
import { FaClock } from "react-icons/fa6";
import { FaFileContract } from "react-icons/fa6";
import { FaMoneyBill } from "react-icons/fa6";
import { IoMdCheckmark } from "react-icons/io";

// Engineering Page
export const engineeringPage = {
    meta: {
        id: "engineering-page",
    },
    hero: {
        image: "../svgs/enginering_hero_mechanical-isometric.svg",
        headline: "Professional Engineering Services",
        subheadline:
            "Add skilled mechanical or plumbing engineers to your team for extra capacity.",
        primaryButton: {
            text: "Get Started",
            type: "primary",
        },
        secondaryButton: {
            text: "Get Estimate",
            type: "secondary",
        },
    },
    planCard: {
        title: "Engineering Packages",
        cardType: "package",
        plans: [
            {
                id: "plan-engr-basic",
                header: "Basic",
                description:
                    "Great for small guidance tasks and minor engineering support.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-engr-standard",
                header: "Standard",
                description:
                    "Perfect for mid-sized projects needing detailed markups and coordination.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-engr-premium",
                header: "Premium",
                description:
                    "Designed for full permit-stage support and comprehensive engineering services.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
        ],
        features: [
            {
                id: "feature-engr-1",
                icon: <FaMoneyBill />,
                text: "Flexible Pricing",
            },
            {
                id: "feature-engr-2",
                icon: <FaFileContract />,
                text: "No Long-Term Contracts",
            },
            {
                id: "feature-engr-3",
                icon: <FaBarsProgress />,
                text: "Regular Project Updates",
            },
            {
                id: "feature-engr-4",
                icon: <FaClock />,
                text: "Start Within 24-48 Hours",
            },
        ],
    },
    articles: [
        {
            id: "article-engr-1",
            reverse: false,
            image: "../svgs/engineering_support_mechanical-floor-plan.svg",
            headline: "Project Engineering Support",
            description:
                "Get temporary engineering resources to handle workload spikes, complex designs, or specialized expertise.",
        },
        {
            id: "article-engr-2",
            reverse: true,
            image: "../svgs/engineering_disciplines_stacked-discipline-views.svg",
            headline: "Expert Guidance Across Disciplines",
            description:
                "Our engineers assist with mechanical, plumbing, and HVAC design reviews, calculations, and coordination tasks.",
        },
        {
            id: "article-engr-3",
            reverse: false,
            image: "../svgs/engineering_team_top-view-systems.svg",
            headline: "Integrated With Your Team",
            description:
                "Work seamlessly with your in-house team to maintain project standards, deadlines, and quality control.",
        },
    ],
    comparisonTable: {
        transpose: true,
        transposeHeader: ["Features", "Basic", "Standard", "Premium"],
        object: [
            {
                "Engineering Discipline": "Mechanical or Plumbing",
                "Engineering Role": "Concept guidance & markups",
                "Basis of Design Input": <IoMdCheckmark />,
                "Engineering Markups (Plans / Details)": <IoMdCheckmark />,
                "System Sizing Logic (Non-Load)": "Preliminary",
                "Equipment Selection Guidance": "—",
                "Code Interpretation Support": "Limited",
                "Coordination Clarifications (MEP)": "Limited",
                "Engineering Calculations (Non-Load)": "Limited",
                "Revision Cycles Included": "1",
                "Sheets Marked Up or Supported": "Up to 5",
                "Permit / AHJ Comment Support": "—",
                "Construction Administration (RFIs)": "—",
                "Accelerated Turnaround": "—",
            },
            {
                "Engineering Discipline": "Mechanical or Plumbing",
                "Engineering Role": "Detailed design markups",
                "Basis of Design Input": <IoMdCheckmark />,
                "Engineering Markups (Plans / Details)": <IoMdCheckmark />,
                "System Sizing Logic (Non-Load)": <IoMdCheckmark />,
                "Equipment Selection Guidance": <IoMdCheckmark />,
                "Code Interpretation Support": <IoMdCheckmark />,
                "Coordination Clarifications (MEP)": <IoMdCheckmark />,
                "Engineering Calculations (Non-Load)": <IoMdCheckmark />,
                "Revision Cycles Included": "2",
                "Sheets Marked Up or Supported": "Up to 12",
                "Permit / AHJ Comment Support": "Limited",
                "Construction Administration (RFIs)": "—",
                "Accelerated Turnaround": "—",
            },
            {
                "Engineering Discipline": "Mechanical or Plumbing",
                "Engineering Role": "Permit-stage technical support",
                "Basis of Design Input": <IoMdCheckmark />,
                "Engineering Markups (Plans / Details)": <IoMdCheckmark />,
                "System Sizing Logic (Non-Load)": <IoMdCheckmark />,
                "Equipment Selection Guidance": <IoMdCheckmark />,
                "Code Interpretation Support": <IoMdCheckmark />,
                "Coordination Clarifications (MEP)": <IoMdCheckmark />,
                "Engineering Calculations (Non-Load)": <IoMdCheckmark />,
                "Revision Cycles Included": "3",
                "Sheets Marked Up or Supported": "Up to 25+",
                "Permit / AHJ Comment Support": <IoMdCheckmark />,
                "Construction Administration (RFIs)": <IoMdCheckmark />,
                "Accelerated Turnaround": <IoMdCheckmark />,
            },
        ],
    },
    faq: [
        {
            id: "faq-engr-1",
            header: "What types of engineering support are available?",
            content:
                "We provide mechanical and plumbing engineering support, including design, calculations, and review services.",
        },
        {
            id: "faq-engr-2",
            header: "Who performs the engineering work?",
            content:
                "All work is performed by licensed Professional Engineers with experience in MEP design and construction projects.",
        },
        {
            id: "faq-engr-3",
            header: "How quickly can engineers start?",
            content:
                "Engineers are typically assigned within 1–2 business days after the request is submitted.",
        },
        {
            id: "faq-engr-4",
            header: "Can engineers work remotely?",
            content:
                "Yes. Our engineers work remotely, depending on project needs and coordination requirements.",
        },
        {
            id: "faq-engr-5",
            header: "Are deliverables reviewed for accuracy?",
            content:
                "All deliverables undergo quality checks and are reviewed by senior engineers to ensure compliance and correctness.",
        },
        {
            id: "faq-engr-6",
            header: "Is ongoing support available?",
            content:
                "Yes. You can request continued support or multiple engineers as needed to meet project deadlines.",
        },
    ],
    cta: {
        label: "Explore Engineering Services",
        headline: "Scale Your Team with Expert Engineers",
        description:
            "Add professional engineers to your workflow on demand and maintain consistent quality across all projects.",
    },
};
